import { useGetMultiOrgs } from '@/lib/api/hooks/use-multi-orgs';
import {
  DEFAULT_ORGANIZATION_ID,
  recoverOrgAccessBySwitchingToDefault,
  shouldRecoverOrgAccess,
} from '@/lib/auth/org-access';
import { useGetAccount } from '@/modules/profile/hooks/use-account';
import { useAuthStore } from '.';
import { useEffect, useRef } from 'react';
import { useErrorHandler } from '@/hooks/use-error-handler';

export const Guard = ({ children }: { children: React.ReactNode }) => {
  const { data, isSuccess, error } = useGetAccount();
  const { setUser, isAuthenticated, selectedOrgId } = useAuthStore();
  const { handleError } = useErrorHandler();
  const { data: orgsData, error: orgsError, isSuccess: isOrgsSuccess } = useGetMultiOrgs({
    enabled: isAuthenticated && isSuccess,
  });
  const lastErrorRef = useRef<any>(null);
  const isRecoveringOrgAccessRef = useRef(false);

  useEffect(() => {
    if (error && error !== lastErrorRef.current) {
      lastErrorRef.current = error;
      handleError(error);
      return;
    }

    if (!isAuthenticated || !isSuccess) return;
    setUser(data || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isAuthenticated, isSuccess, error, setUser]);

  useEffect(() => {
    if (
      !isAuthenticated ||
      !isSuccess ||
      isRecoveringOrgAccessRef.current ||
      !selectedOrgId ||
      selectedOrgId === DEFAULT_ORGANIZATION_ID
    ) {
      return;
    }

    const canEvaluateOrgAccess = Boolean(orgsError) || isOrgsSuccess;
    if (!canEvaluateOrgAccess) {
      return;
    }

    if (!shouldRecoverOrgAccess(selectedOrgId, orgsError, orgsData, data)) {
      return;
    }

    isRecoveringOrgAccessRef.current = true;
    void recoverOrgAccessBySwitchingToDefault().catch(() => {
      isRecoveringOrgAccessRef.current = false;
    });
  }, [isAuthenticated, isSuccess, orgsError, isOrgsSuccess, orgsData, data, selectedOrgId]);

  return <>{children}</>;
};
